<template>
  <v-overlay v-if="!showFormContent" z-index="10">
    <v-progress-circular indeterminate size="64" />
  </v-overlay>
  <DialogFull
    v-else
    :data="dialogSettings"
    :steps="steps"
    @onStepBack="stepBack"
    :hideCloseButton="isUserIncomplete"
    @close="onClose"
    :backButtonAlwaysVisible="checkForBackButtonVisibility && showBackButton"
    :showLogoutButton="isUserIncomplete"
    customActions
  >
    <template v-slot:dialog.body>
      <JobInfoBar
        v-if="isJobTypeForm && currentBooking"
        :title="currentBooking.title"
      />
      <div class="container col-md-8 col-sm-12">
        <div v-if="isLoading">
          <v-skeleton-loader type="text" class="my-10" />
          <v-skeleton-loader
            v-for="i in 3"
            :key="i"
            type="list-item-three-line"
          />
        </div>
        <div v-else>
          <v-progress-circular
            class="mr-2 mb-5"
            size="40"
            :value="getStepProgressValue(step)"
            color="primary"
            v-for="(step, i) in totalSteps"
            :key="`${i}-step`"
            :rotate="270"
          >
            {{ step }}
          </v-progress-circular>
          <div v-for="(step, index) in allSteps" :key="index">
            <component
              :is="step.component"
              @onFinishEditingProfile="handleFinishEditingProfile"
              :items="step.items || []"
              :ref="`step-${index + 1}`"
              @onStepChanged="handleStepChanged"
              v-if="
                shouldStepViewBeShown(index) ||
                  ifStepHasAlreadyBeenCompleted(index)
              "
              v-show="shouldStepViewBeShown(index)"
            />
          </div>
        </div>
      </div>
    </template>
  </DialogFull>
</template>

<script>
import DialogFull from "@/components/common/DialogFull";
import { APPLICATION_NAMESPACE } from "@/store/modules/applications";
import {
  GET_BOOKING_APPLICATION_QUESTIONS,
  GET_CURRENT_USER_APPLICATION
} from "@/store/modules/applications/getters";
import {
  UPDATE_APPLICATION,
  FETCH_BOOKING_APPLICATION_QUESTIONS,
  FETCH_USER_APPLICATION,
  CREATE_APPLICATION
} from "@/store/modules/applications/actions";
import { createNamespacedHelpers } from "vuex";
import BaseQuestions from "@/views/profile/QuestionsAndAnswers/steps/BaseQuestions";
import JobInfoBar from "@/views/profile/components/JobInfoBar";
import { forEach, size, first, some, isEmpty, includes, find } from "lodash";
import Qualifications from "@/views/profile/QuestionsAndAnswers/steps/Qualifications/Qualifications";
import Experience from "@/views/profile/QuestionsAndAnswers/steps/Experience/Experience";
import CV from "@/views/profile/QuestionsAndAnswers/steps/CV";
import LanguagesAndSkills from "@/views/profile/QuestionsAndAnswers/steps/LanguagesAndSkills/LanguagesAndSkills";
import ContactDetails from "@/views/profile/QuestionsAndAnswers/steps/ContactDetails/ContactDetails";
import CriminalConvictions from "@/views/profile/QuestionsAndAnswers/steps/CriminalConvictions";
import Diversity from "@/views/profile/QuestionsAndAnswers/steps/Diversity/Diversity";
import ApplicationConfirmation from "@/views/profile/QuestionsAndAnswers/steps/ApplicationConfirmation";
import { AUTH_NAMESPACE } from "@/store/modules/auth";
import {
  SELECT_PROFILE,
  FETCH_SELF_DETAILS
} from "@/store/modules/auth/action-types";
import { USERS_NAMESPACE } from "@/store/modules/users";
import { CREATE_WORKER_ACCOUNT } from "@/store/modules/users/actions";
import { USER_STATUSES } from "@/constants/users";
import {
  BOOKINGS_NAMESPACE,
  FETCH_CURRENT_BOOKING,
  FETCH_BOOKING_OPTIONS
} from "@/store/modules/bookings/actions";
import {
  GET_CURRENT_BOOKING,
  GET_BOOKING_OPTIONS,
  IS_FETCHING_BOOKING_OPTIONS
} from "@/store/modules/bookings/getters";
import { APPLICATION_STATUSES } from "@/utils/bookings";
import { isPermissioned } from "@/utils/permissions";
import moment from "moment";
import { PERMISSIONS } from "@/constants/workers";
import { isWorker } from "@/utils/users";

const {
  mapActions: mapApplicationActions,
  mapGetters: mapApplicationGetters
} = createNamespacedHelpers(APPLICATION_NAMESPACE);
const {
  mapState: mapAuthState,
  mapActions: mapAuthActions
} = createNamespacedHelpers(AUTH_NAMESPACE);
const {
  mapActions: mapBookingActions,
  mapGetters: mapBookingGetters
} = createNamespacedHelpers(BOOKINGS_NAMESPACE);
const { mapActions: mapUserActions } = createNamespacedHelpers(USERS_NAMESPACE);

const NEW_ACCOUNT_FORM_TYPES = {
  PROFILE: "profile",
  JOB: "job"
};

export default {
  title: "Track",
  components: {
    DialogFull,
    JobInfoBar
  },
  async created() {
    this.isUserIncomplete = this.self.status === USER_STATUSES.INCOMPLETE;
    if (this.isNonWorkerProfile && this.bookingId) {
      return this.handleNonWorkerProfile();
    }
    // form content should be shown if there is no reason to create worker profile
    this.showFormContent = true;
    this.adjustFormSteps();
  },
  data() {
    return {
      checkForBackButtonVisibility: false,
      dialogSettings: {
        dialog: true
      },
      stepProgressValue: 0,
      steps: {
        current: 1,
        saved: 0
      },
      isLoading: false,
      showFormContent: false,
      requirementQuestions: [],
      nonRequirementQuestions: [],
      formType: NEW_ACCOUNT_FORM_TYPES.PROFILE,
      isUserIncomplete: false
    };
  },
  computed: {
    ...mapAuthState({
      self: state => state.userData,
      profiles: state => state.profiles
    }),
    ...mapApplicationGetters({
      bookingApplicationQuestions: GET_BOOKING_APPLICATION_QUESTIONS,
      currentUserApplication: GET_CURRENT_USER_APPLICATION
    }),
    ...mapBookingGetters({
      currentBooking: GET_CURRENT_BOOKING,
      bookingOptions: GET_BOOKING_OPTIONS,
      isFetchingBookingOptions: IS_FETCHING_BOOKING_OPTIONS
    }),
    bookingId() {
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);
      return urlParams.get("booking_id");
    },
    showConfirmationPage() {
      return this.isJobTypeForm;
    },
    isNonWorkerProfile() {
      return !isWorker(this.self.accountType);
    },
    isJobTypeForm() {
      return this.formType === NEW_ACCOUNT_FORM_TYPES.JOB;
    },
    showRequirementQuestions() {
      return this.isJobTypeForm && size(this.requirementQuestions);
    },
    showNonRequirementQuestions() {
      return this.isJobTypeForm && size(this.nonRequirementQuestions);
    },
    allSteps() {
      return [
        this.showRequirementQuestions && {
          component: BaseQuestions,
          items: this.requirementQuestions
        },
        this.showStep(PERMISSIONS.CONTACT_DETAILS) && {
          component: ContactDetails
        },
        (this.showStep(PERMISSIONS.CV) || this.requireCv) && { component: CV },
        (this.showStep(PERMISSIONS.QUALIFICATIONS) ||
          this.requireQualifications) && {
          component: Qualifications
        },
        (this.showStep(PERMISSIONS.EXPERIENCE) || this.requireExperience) && {
          component: Experience
        },
        this.showNonRequirementQuestions && {
          component: BaseQuestions,
          items: this.nonRequirementQuestions
        },
        this.showStep(PERMISSIONS.CRIMINAL_CONVICTIONS) && {
          component: CriminalConvictions
        },
        this.showStep(PERMISSIONS.LANGUAGES_AND_SKILLS) && {
          component: LanguagesAndSkills
        },
        this.showStep(PERMISSIONS.DIVERSITY) && { component: Diversity },
        this.showConfirmationPage && { component: ApplicationConfirmation }
      ].filter(Boolean);
    },
    totalSteps() {
      return size(this.allSteps);
    },
    showBackButtonInCurrentStep() {
      // It's possible step contains nested steps inside. If so, we should show them the back button
      const currentStep = first(this.$refs[`step-${this.steps.current}`]);
      return currentStep && currentStep.showBackButton;
    },
    showBackButton() {
      return this.steps.current > 1 || this.showBackButtonInCurrentStep;
    },
    doesUserApplicationIsAlreadyFinished() {
      return (
        this.currentUserApplication &&
        this.currentUserApplication.status !== APPLICATION_STATUSES.DRAFT
      );
    },
    utm_source() {
      return this.$route.query.utm_source;
    },
    afterDeadlineApplyKey() {
      return this.$route.query.adak;
    },
    applicationParamsFromUrl() {
      return {
        ...(this.utm_source && { utm_source: this.utm_source }),
        ...(this.afterDeadlineApplyKey && {
          afterDeadlineApplyKey: this.afterDeadlineApplyKey
        })
      };
    },
    ifUrlIncludesApplicationParams() {
      return !isEmpty(this.applicationParamsFromUrl);
    },
    requireCv() {
      return this.bookingOptions && this.bookingOptions.requireCv;
    },
    requireExperience() {
      return this.bookingOptions && this.bookingOptions.requireExperience;
    },
    requireQualifications() {
      return this.bookingOptions && this.bookingOptions.requireQualifications;
    },
    isClosedBooking() {
      return Boolean(this.currentBooking.closedDate);
    }
  },
  methods: {
    ...mapAuthActions({
      selectProfile: SELECT_PROFILE,
      fetchSelfDetails: FETCH_SELF_DETAILS
    }),
    ...mapApplicationActions({
      fetchBookingApplicationQuestions: FETCH_BOOKING_APPLICATION_QUESTIONS,
      fetchUserApplication: FETCH_USER_APPLICATION,
      createApplication: CREATE_APPLICATION,
      updateApplication: UPDATE_APPLICATION
    }),
    ...mapBookingActions({
      fetchCurrentBooking: FETCH_CURRENT_BOOKING,
      fetchBookingOptions: FETCH_BOOKING_OPTIONS
    }),
    ...mapUserActions({
      createWorkerAccount: CREATE_WORKER_ACCOUNT
    }),
    async redirectToInvalidBookingPage() {
      this.$router.push("/invalid-booking");
    },
    async adjustFormSteps() {
      this.isLoading = true;
      const canContinue = await this.adjustProfileFormType();
      if (!canContinue) {
        return;
      }
      if (this.isJobTypeForm) {
        await this.adjustBookingApplicationQuestions();
      }
      this.isLoading = false;
      if (
        !this.totalSteps &&
        !this.isUserIncomplete &&
        !this.doesUserApplicationIsAlreadyFinished
      ) {
        this.$router.push("/settings/profile");
      }
    },
    async adjustBookingApplicationQuestions() {
      await this.fetchBookingApplicationQuestions(this.bookingId);
      forEach(this.bookingApplicationQuestions, question => {
        if (question.required) {
          this.requirementQuestions.push(question);
        } else {
          this.nonRequirementQuestions.push(question);
        }
      });
    },
    getBookingIdFromUrl() {
      const queryString = window.location.search;
      const urlParams = new URLSearchParams(queryString);
      return urlParams.get("booking_id");
    },
    isBookingDateInvalid() {
      return (
        !this.currentBooking ||
        !moment(this.currentBooking.deadlineDate).isSameOrAfter(moment(), "day")
      );
    },
    async adjustProfileFormType() {
      let canContinue = true;
      if (this.bookingId) {
        await this.fetchCurrentBooking(this.bookingId);
        await this.fetchBookingOptions(this.bookingId);
        if (
          (this.isBookingDateInvalid() && !this.afterDeadlineApplyKey) ||
          this.isClosedBooking
        ) {
          canContinue = false;
          return this.redirectToInvalidBookingPage();
        }
        await this.adjustFormTypeByApplication();
      } else {
        this.dialogSettings.title = "Create Profile";
        document.title = `${this.dialogSettings.title} - Track`;
      }
      return canContinue;
    },
    async adjustFormTypeByApplication() {
      await this.fetchUserApplication({
        bookingId: this.bookingId,
        userId: this.self.id
      });
      if (this.doesUserApplicationIsAlreadyFinished) {
        return this.navigateToCurrentApplication();
      }
      if (!this.currentUserApplication) {
        await this.handleNewApplication();
      } else if (this.ifUrlIncludesApplicationParams) {
        await this.updateApplication({
          id: this.currentUserApplication.id,
          ...this.applicationParamsFromUrl
        });
      }
      this.formType = NEW_ACCOUNT_FORM_TYPES.JOB;
      this.adjustPageParams();
    },
    stepBack() {
      first(this.$refs[`step-${this.steps.current}`]).stepBack();
    },
    onClose() {
      if (this.bookingId) {
        this.$router.push({
          name: "bookingDetails",
          id: this.bookingId
        });
      } else {
        this.$router.push({
          name: "dashboard"
        });
      }
    },
    showStep(permissions) {
      return some(Object.values(permissions), permissionToCheck =>
        isPermissioned(
          `${this.getPermissionNamespace(
            permissionToCheck
          )}.${permissionToCheck}`
        )
      );
    },
    getPermissionNamespace(permission) {
      const applicationPermissions = ["cv", "experience", "qualifications"];
      if (includes(applicationPermissions, permission) && this.isJobTypeForm) {
        if (this.currentUserApplication) {
          return `applications[${this.currentUserApplication.id}].view`;
        }
        return "applications.view";
      }

      return "workers.edit";
    },
    getStepProgressValue(step) {
      if (step < this.steps.current) {
        return 100;
      }
      if (step === this.steps.current) {
        return this.stepProgressValue;
      }
      return 0;
    },
    handleStepChanged({ progressValue }) {
      this.checkForBackButtonVisibility = true;
      this.stepProgressValue = progressValue;
      if (progressValue === 0) {
        this.stepProgressValue = 100;
        this.steps.current--;
      }
      if (progressValue > 100) {
        this.stepProgressValue = 0;
        this.stepForward();
      }
    },
    async stepForward() {
      this.steps.current += 1;
      if (
        this.steps.saved < this.totalSteps &&
        this.steps.current > this.steps.saved
      ) {
        this.steps.saved += 1;
      }
    },
    shouldStepViewBeShown(index) {
      const viewStep = index + 1;
      return viewStep <= this.totalSteps && this.steps.current === viewStep;
    },
    ifStepHasAlreadyBeenCompleted(index) {
      const viewStep = index + 1;
      return viewStep <= this.steps.saved;
    },
    navigateToCurrentApplication() {
      this.$router.push({
        name: "applicationTabs",
        params: { id: this.currentUserApplication.id }
      });
    },
    async handleNewApplication() {
      if (isPermissioned("applications.create")) {
        await this.createApplication({
          user: { id: this.self.id },
          booking: { id: this.bookingId },
          ...this.applicationParamsFromUrl
        });
      } else {
        this.$router.push("switch-account");
      }
    },
    adjustPageParams() {
      this.dialogSettings.title = "New Application";
      document.title = `${this.currentBooking.title} - ${this.dialogSettings.title} - Track`;
    },
    getUserWorkerProfile() {
      return find(this.profiles, ({ roles }) => isWorker(roles));
    },
    async handleNonWorkerProfile() {
      const userWorkerProfile = this.getUserWorkerProfile();
      if (userWorkerProfile) {
        await this.selectProfile({ profile: userWorkerProfile, refresh: true });
      } else {
        const newUserWorkerProfile = await this.createWorkerAccount();
        if (newUserWorkerProfile) {
          await this.selectProfile({
            profile: newUserWorkerProfile
          });
          this.fetchSelfDetails();
        }
      }
    },
    handleFinishEditingProfile() {
      if (!this.isJobTypeForm) {
        this.$router.push({
          name: "profileSubmitted",
          params: {
            isCreatingProfile: true
          }
        });
      }
    },
    size
  }
};
</script>

<style lang="scss">
.application-info-bar {
  align-items: center;
  display: flex;
  justify-content: center;
  background-color: $background;
  width: 100%;
  height: 60px;
}
</style>
